import api from '@/services/api';
import type {
  Resource,
  ResourceSchedule,
  ScheduleException,
  CreateResourceRequest,
  UpdateResourceRequest,
  CreateScheduleRequest,
  CreateExceptionRequest,
  ResourceQueryParams,
  ProcedureType,
} from '@/types';

const BASE_PATH = '/resources';

/**
 * Resource management API service.
 *
 * Handles CRUD operations for resources (doctors, rooms, equipment),
 * their weekly schedules, exceptions, and linked procedure types.
 */
export const resourceService = {
  /**
   * Lists all resources, optionally filtered by type or active status.
   */
  async getResources(params?: ResourceQueryParams): Promise<Resource[]> {
    const { data } = await api.get<{ items: Resource[] }>(BASE_PATH, { params });
    return data.items;
  },

  /**
   * Fetches a single resource by ID, including schedules.
   */
  async getResource(id: string): Promise<Resource> {
    const { data } = await api.get<Resource>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Creates a new resource.
   */
  async createResource(payload: CreateResourceRequest): Promise<Resource> {
    const { data } = await api.post<Resource>(BASE_PATH, payload);
    return data;
  },

  /**
   * Updates an existing resource.
   */
  async updateResource(id: string, payload: UpdateResourceRequest): Promise<Resource> {
    const { data } = await api.put<Resource>(`${BASE_PATH}/${id}`, payload);
    return data;
  },

  /**
   * Activates a resource.
   */
  async activateResource(id: string): Promise<Resource> {
    const { data } = await api.put<Resource>(`${BASE_PATH}/${id}/activate`);
    return data;
  },

  /**
   * Deactivates a resource.
   */
  async deactivateResource(id: string): Promise<Resource> {
    const { data } = await api.put<Resource>(`${BASE_PATH}/${id}/deactivate`);
    return data;
  },

  /**
   * Adds a weekly schedule entry to a resource.
   */
  async addSchedule(
    resourceId: string,
    payload: CreateScheduleRequest,
  ): Promise<ResourceSchedule> {
    const { data } = await api.post<ResourceSchedule>(
      `${BASE_PATH}/${resourceId}/schedules`,
      payload,
    );
    return data;
  },

  /**
   * Removes a weekly schedule entry from a resource.
   */
  async removeSchedule(resourceId: string, scheduleId: string): Promise<void> {
    await api.delete(`${BASE_PATH}/${resourceId}/schedules/${scheduleId}`);
  },

  /**
   * Adds a schedule exception (block, partial block, or override) to a resource.
   */
  async addException(
    resourceId: string,
    payload: CreateExceptionRequest,
  ): Promise<ScheduleException> {
    const { data } = await api.post<ScheduleException>(
      `${BASE_PATH}/${resourceId}/exceptions`,
      payload,
    );
    return data;
  },

  /**
   * Removes a schedule exception from a resource.
   */
  async removeException(resourceId: string, exceptionId: string): Promise<void> {
    await api.delete(`${BASE_PATH}/${resourceId}/exceptions/${exceptionId}`);
  },

  /**
   * Fetches exceptions for a resource.
   */
  async getExceptions(resourceId: string): Promise<ScheduleException[]> {
    const { data } = await api.get<ScheduleException[]>(
      `${BASE_PATH}/${resourceId}/exceptions`,
    );
    return data;
  },

  /**
   * Fetches procedure types linked to a resource.
   */
  async getLinkedProcedures(resourceId: string): Promise<ProcedureType[]> {
    const { data } = await api.get<ProcedureType[]>(
      `${BASE_PATH}/${resourceId}/procedure-types`,
    );
    return data;
  },

  /**
   * Links a procedure type to a resource.
   */
  async linkProcedure(resourceId: string, procedureTypeId: string): Promise<void> {
    await api.post(`${BASE_PATH}/${resourceId}/procedure-types`, { procedureTypeId });
  },

  /**
   * Unlinks a procedure type from a resource.
   */
  async unlinkProcedure(resourceId: string, procedureTypeId: string): Promise<void> {
    await api.delete(`${BASE_PATH}/${resourceId}/procedure-types/${procedureTypeId}`);
  },
};
